import { promiseTimeout } from '@vueuse/core';

import type { KkmHandshakeReady, KkmServerAdapter } from '@/shared/api';
import type {
  OrderFiscalExecutionFieldsFragment,
  OrderFiscalKkmResponseInput,
  OrderFiscalOperationIdentityInput,
  ResolveOrderFiscalReceiptInput,
} from '@shared/api/graphql/gql/graphql';

import { readFiscalDocuments } from '@/entities/fiscalization';

import type { KktOpeningProof } from './fiscal-shift-actions';

import { proofFromOpenSession } from './fiscal-shift-actions';
import { assertExecutionTarget } from './order-fiscal-result';
import { reportKktShiftDesync } from './shift-kkt-reopen-controller';

/**
 * Общий словарь settlement и refund: живая привязка к подключённой ККТ и
 * фоновый диагноз сбоя. Контроллеры обоих сценариев используют один механизм.
 */

interface FiscalExecutionRef {
  id_command: string;
  operation: { id: string };
  payload_hash: string;
}

/**
 * Идентификатор ККТ, подтверждённый handshake'ом перед стартом операции.
 *
 * Хранится отдельно от execution: сервер должен видеть кассу, на которой
 * команда реально исполняется, а не ту, что была записана при prepare.
 */
export class LiveKktIdentity {
  private deviceId: string | null = null;

  /** Драйвер ККТ и версия KKMServer: сервер требует их, чтобы признать отказ «нет бумаги» доказанным. */
  private driver: string | null = null;

  private serverVersion: string | null = null;

  public capture(ready: KkmHandshakeReady): void {
    this.deviceId = ready.kktDevice.idDevice;
    this.driver = ready.kktDevice.idTypeDevice;
    this.serverVersion = ready.server.serverVersion;
  }

  public clear(): void {
    this.deviceId = null;
    this.driver = null;
    this.serverVersion = null;
  }

  /** Сырой ответ ККТ с контекстом handshake: по ним сервер доказывает, что документ не создан. */
  public kkmResponse(response: OrderFiscalKkmResponseInput['response']): OrderFiscalKkmResponseInput {
    return { driver: this.driver, response, serverVersion: this.serverVersion };
  }

  /** Касса в identity обязательна: без handshake identity не выдаётся. */
  public identityFor(execution: FiscalExecutionRef): OrderFiscalOperationIdentityInput & { kktDeviceId: string } {
    if (this.deviceId === null) {
      throw new Error('Не удалось определить подключённую кассу. Обновите состояние оборудования.');
    }

    return {
      idCommand: execution.id_command,
      kktDeviceId: this.deviceId,
      operationId: execution.operation.id,
      payloadHash: execution.payload_hash,
    };
  }
}

/**
 * Handshake, из которого нужен только успешный исход: недоступная касса и сбой
 * транспорта здесь одинаково значат «состояние ККТ неизвестно».
 */
export async function tryHandshakeReady(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  kktDeviceId?: string | null,
): Promise<KkmHandshakeReady | null> {
  try {
    const handshake = await adapter.handshake({ kktDeviceId: kktDeviceId ?? undefined });

    return handshake.kind === 'ready' ? handshake : null;
  } catch {
    return null;
  }
}

/** Смена закрыта на самой ККТ: `sessionState === 1` — закрытая сессия. */
export function kktSessionClosed(handshake: KkmHandshakeReady | null): boolean {
  return handshake?.kkt.sessionState === 1;
}

/**
 * Шкала повторного чтения состояния смены на ККТ. Новая и намеренно короткая: KkmServer +
 * АТОЛ 35Ф обновляют `SessionState` с задержкой в единицы секунд после фискального документа
 * (21.08 — дольше четырёх минут), а однократное чтение к тому же ловит транзиентный отказ:
 * в прямом поллинге ККТ 22.08 виден одиночный `status: 1` с восстановлением через 7 с.
 *
 * `BACKOFF_STEPS_MS` фонового health-поллинга (`[15s, 30s, 60s, 120s]`) здесь непригодна:
 * та шкала отвечает на «касса недоступна», эта — на «касса ещё не догнала собственный документ».
 */
const KKT_STATE_CONFIRMATION_STEPS_MS = [250, 500, 1_000, 2_000, 3_000] as const;

/**
 * Читает состояние ККТ, пока оно не совпадёт с ожидаемым либо не кончится окно ретраев.
 * Отказ handshake внутри окна — не приговор: попытки продолжаются.
 *
 * `null` означает «за окно ожидания состояние не подтвердилось». Это неопределённость,
 * а не отрицание: решение о ней принимает вызывающий.
 */
function awaitKktState(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  matches: (handshake: KkmHandshakeReady) => boolean,
  kktDeviceId?: string | null,
): Promise<KkmHandshakeReady | null> {
  return pollKktState(adapter, (handshake) => handshake !== null && matches(handshake), kktDeviceId);
}

/**
 * Читает состояние ККТ, повторяя попытки только пока касса не отвечает. Внятный ответ
 * принимается сразу — в отличие от `awaitKktState` здесь нечего догонять: касса ничего
 * не печатала, и `SessionState` не отстаёт от собственного документа. Смысл ретраев один:
 * не выдать транзиентный отказ связи за состояние смены.
 */
export function readKktState(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  kktDeviceId?: string | null,
): Promise<KkmHandshakeReady | null> {
  return pollKktState(adapter, (handshake) => handshake !== null, kktDeviceId);
}

/**
 * Доказательство открытой смены из опроса состояния. Критерий открытой сессии живёт
 * в `proofFromOpenSession` и применяется здесь дважды намеренно: сначала как условие
 * выхода из ретраев, потом как разбор принятого ответа.
 */
export async function awaitOpenSessionProof(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  kktDeviceId?: string | null,
): Promise<KktOpeningProof | null> {
  const handshake = await awaitKktState(adapter, (candidate) => proofFromOpenSession(candidate) !== null, kktDeviceId);

  return handshake === null ? null : proofFromOpenSession(handshake);
}

async function pollKktState(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  accept: (handshake: KkmHandshakeReady | null) => boolean,
  kktDeviceId?: string | null,
): Promise<KkmHandshakeReady | null> {
  let handshake = await tryHandshakeReady(adapter, kktDeviceId);

  if (accept(handshake)) {
    return handshake;
  }

  for (const delayMs of KKT_STATE_CONFIRMATION_STEPS_MS) {
    await promiseTimeout(delayMs);
    handshake = await tryHandshakeReady(adapter, kktDeviceId);

    if (accept(handshake)) {
      return handshake;
    }
  }

  return null;
}

/**
 * После сбоя команды проверяет, не закрыли ли смену на самой кассе — тогда
 * оператору нужно переоткрытие, а не повтор. Читается ККТ операции: при двух
 * ККТ на KKMServer handshake без устройства не отвечает.
 */
export async function kktShiftClosedAfterFailure(
  adapter: Pick<KkmServerAdapter, 'handshake'>,
  operationId: string,
  kktDeviceId: string,
): Promise<boolean> {
  if (!kktSessionClosed(await tryHandshakeReady(adapter, kktDeviceId))) {
    return false;
  }

  reportKktShiftDesync({ operationId });

  return true;
}

type ReceiptDocumentsRead =
  | { kind: 'unavailable' }
  | { kind: 'unreadable' }
  | { kind: 'ready'; input: Pick<ResolveOrderFiscalReceiptInput, 'documents' | 'fiscalDriveNumber'> };

/** Проверяет кассу операции и читает тот же ФН; заменённый ФН даёт серверу пустой набор. */
export async function readExecutionReceiptDocuments(
  adapter: Pick<KkmServerAdapter, 'getDataCheck'>,
  execution: Pick<OrderFiscalExecutionFieldsFragment, 'same_fiscal_drive' | 'execution_target' | 'fiscal_scan_from'>,
  liveKkt: LiveKktIdentity,
  handshake: () => ReturnType<KkmServerAdapter['handshake']>,
): Promise<ReceiptDocumentsRead> {
  if (execution.same_fiscal_drive === false) {
    return { kind: 'ready', input: { documents: [], fiscalDriveNumber: null } };
  }
  const ready = await handshake();
  if (ready.kind !== 'ready') return { kind: 'unavailable' };
  assertExecutionTarget(execution, ready, 'Подключите кассу операции.');
  liveKkt.capture(ready);
  const documents = await readFiscalDocuments(
    adapter,
    execution.execution_target.kkt_device_id,
    execution.fiscal_scan_from ?? 1,
    execution.execution_target.timezone,
  );
  return documents === null
    ? { kind: 'unreadable' }
    : { kind: 'ready', input: { documents, fiscalDriveNumber: ready.kkt.fnNumber } };
}
